import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { suggestBooks, findBook, parseReference, formatReference, TRANSLATIONS } from '../lib/bible'
import { Check } from './ui'
import Passage from './Passage'

const range = (n) => Array.from({ length: n }, (_, i) => i + 1)

/**
 * Book → chapter → verses, then the passage, then "does this look right?".
 *
 * Chapter and verse are dropdowns bounded by what actually exists, so an
 * impossible reference cannot be typed in the first place. The verse count is
 * fetched per chapter rather than assumed.
 */
export default function ScripturePicker({ value, translation, text, verses, onChange }) {
  const parsed = parseReference(value || '') || {}
  const [book, setBook] = useState(parsed.book || '')
  const [query, setQuery] = useState(parsed.book || '')
  const [chapter, setChapter] = useState(parsed.chapter || '')
  const [vs, setVs] = useState(parsed.verseStart || '')
  const [ve, setVe] = useState(parsed.verseEnd || '')
  const [trans, setTrans] = useState(translation?.toLowerCase() || 'kjv')
  const [open, setOpen] = useState(false)
  const [verseCount, setVerseCount] = useState(null)
  const [counting, setCounting] = useState(false)
  const [passage, setPassage] = useState(text ? { text, verses, translation } : null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const box = useRef(null)

  const chosen = findBook(book)
  const reference = formatReference({
    book: chosen?.name, chapter: Number(chapter) || null,
    verseStart: Number(vs) || null, verseEnd: Number(ve) || null,
  })
  const suggestions = open && query !== book ? suggestBooks(query) : []

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [])

  // How many verses are actually in this chapter. Until we know, the verse
  // pickers stay closed rather than offering numbers that may not exist.
  useEffect(() => {
    let alive = true
    setVerseCount(null)
    if (!chosen || !chapter) return
    setCounting(true)
    supabase.functions
      .invoke('bible-verse', { body: { mode: 'chapter_info', book: chosen.name, chapter: Number(chapter) } })
      .then(({ data, error }) => {
        if (!alive) return
        setCounting(false)
        if (error || data?.error) return setErr(data?.error || 'Could not read that chapter.')
        setErr(null)
        setVerseCount(data.verses)
        setVs((v) => (Number(v) > data.verses ? '' : v))
        setVe((v) => (Number(v) > data.verses ? '' : v))
      })
    return () => { alive = false }
  }, [chosen?.name, chapter]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPassage(null) }, [reference, trans])

  const pick = (b) => {
    setBook(b.name); setQuery(b.name); setOpen(false)
    setChapter(b.chapters === 1 ? 1 : ''); setVs(''); setVe(''); setVerseCount(null)
  }

  const look = async () => {
    if (!reference) return
    setBusy(true); setErr(null)
    const { data, error } = await supabase.functions.invoke('bible-verse', {
      body: { reference, translation: trans },
    })
    setBusy(false)
    if (error || data?.error) return setErr(data?.error || 'Could not look that up.')
    setPassage(data)
  }

  const confirm = () => onChange({
    reference,
    translation: passage.translation,
    text: passage.text,
    verses: passage.verses ?? null,
  })

  return (
    <div className="field" ref={box}>
      <label className="label">Scripture it came from</label>

      <div style={{ position: 'relative' }}>
        <input className="input" value={query} placeholder="Start typing a book — Ps, Ruth, 1 Cor…"
               onChange={(e) => { setQuery(e.target.value); setBook(''); setOpen(true) }}
               onFocus={() => setOpen(true)} />
        {suggestions.length > 0 && (
          <div className="booklist">
            {suggestions.map((b) => (
              <button key={b.name} className="bookopt" onMouseDown={() => pick(b)}>
                {b.name}<span>{b.chapters === 1 ? 'one chapter' : `${b.chapters} ch`}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {chosen && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          {chosen.chapters > 1 && (
            <div style={{ flex: 1.2 }}>
              <label className="label" style={{ fontSize: 10 }}>Chapter</label>
              <select className="input" value={chapter} onChange={(e) => {
                setChapter(e.target.value); setVs(''); setVe('')
              }}>
                <option value="">—</option>
                {range(chosen.chapters).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div style={{ flex: 1 }}>
            <label className="label" style={{ fontSize: 10 }}>Verse</label>
            <select className="input" value={vs} disabled={!verseCount}
                    onChange={(e) => { setVs(e.target.value); setVe('') }}>
              <option value="">{counting ? '…' : verseCount ? 'all' : '—'}</option>
              {range(verseCount || 0).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label className="label" style={{ fontSize: 10 }}>To</label>
            <select className="input" value={ve} disabled={!verseCount || !vs}
                    onChange={(e) => setVe(e.target.value)}>
              <option value="">—</option>
              {/* only verses at or after the start, and only ones that exist */}
              {range(verseCount || 0).filter((v) => v >= Number(vs)).map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {verseCount && chapter && (
        <p className="small" style={{ marginTop: 6 }}>
          {chosen.name} {chapter} has {verseCount} verse{verseCount === 1 ? '' : 's'}.
        </p>
      )}

      {chosen && chapter && verseCount && (
        <>
          <div className="chips" style={{ marginTop: 12 }}>
            {TRANSLATIONS.map((t) => (
              <button key={t.id} className={`chip${trans === t.id ? ' on' : ''}`}
                      title={t.note} onClick={() => setTrans(t.id)}>{t.label}</button>
            ))}
          </div>

          {!passage && (
            <button className="btn btn-quiet" style={{ marginTop: 12 }} disabled={busy} onClick={look}>
              {busy ? 'Looking it up…' : `Look up ${reference}`}
            </button>
          )}
        </>
      )}

      {passage && (
        <div style={{ marginTop: 12 }}>
          <div className="small" style={{ marginBottom: 6 }}>Does this look right?</div>
          <Passage verses={passage.verses} text={passage.text}
                   reference={reference} translation={passage.translation} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={confirm}><Check /> Yes, that's it</button>
            <button className="btn btn-quiet" onClick={() => setPassage(null)}>No, change it</button>
          </div>
        </div>
      )}

      {err && <p className="err">{err}</p>}

      {value && !passage && (
        <p className="small" style={{ marginTop: 8 }}>
          Saved as <b>{value}</b>{translation ? ` (${translation})` : ''}.
        </p>
      )}
    </div>
  )
}
